import { ReactElement, ReactNode, useMemo, useState } from "react";

import {
    getSearchFromMetadata,
    BsMetaUtil,
    TokenBalanceExpanded,
    useAllAvailablePrices,
    isWhirlpoolMetadata
} from "@bridgesplit/abf-react";
import {
    Column,
    FONT_SIZES,
    Icon,
    Image,
    OverlappingImages,
    Row,
    SearchInput,
    SkeletonRounded,
    Text,
    TextProps,
    TooltipText,
    repeatElement,
    Tooltip,
    OverlappingImagesProps,
    VerticalScroll,
    MEDIA,
    SxType,
    SelectOption,
    Select,
    TextExternalLink,
    TextButton,
    ExternalLink,
    TextVariant,
    TextColor,
    Dot,
    BRAND_COLORS
} from "@bridgesplit/ui";
import { ClmmPositionExpanded, LoopRouteType, TokenListMetadata } from "@bridgesplit/abf-sdk";
import {
    ALL_VALUE,
    bsMath,
    explorerTokenUrl,
    formatUsd,
    greaterThan,
    removeDuplicatesByProperty,
    textContains
} from "@bridgesplit/utils";
import { useMediaQuery } from "@mui/material";
import { ArrowOutwardOutlined } from "@mui/icons-material";
import { AppDialog, useAppDialog } from "app/utils";

import { ClmmPositionInfo } from "./clmm";
import { formatTokenName } from "../loop/detail/util";

export function AssetRows({
    assets,
    isLoading
}: {
    assets:
        (TokenBalanceExpanded & { alertTooltip?: string; clmmPosition?: ClmmPositionExpanded | null })[] | undefined;
    isLoading?: boolean;
}) {
    const { getPriceByMint } = useAllAvailablePrices(assets?.map((c) => c.metadata.mint));

    const pricesAvailable = assets?.map((c) => getPriceByMint(c.metadata.mint)).some((price) => !!price);

    if (assets?.length === 0 && !isLoading)
        return (
            <Tooltip title="Due to a major update to our loan event system, historical collateral data is not available for older loans">
                <Text color="disabled">No collateral history</Text>
            </Tooltip>
        );

    return (
        <Column sx={{ mx: -1, mr: 1 }}>
            {(!assets || isLoading) && repeatElement(<SkeletonRounded height={40} sx={{ mx: 1, my: 1 }} />)}
            {!isLoading &&
                assets?.map(({ clmmPosition, metadata, amount, alertTooltip }, i) => {
                    return (
                        <Row
                            sx={{ width: "100%", px: 1, py: 1 }}
                            spaceBetween
                            key={metadata.mint + `${i}-asset-row-collateral`}
                        >
                            <Row spacing={1}>
                                <TokenImage size="lg" metadata={metadata} />
                                <Column>
                                    <TooltipText icon={false} helpText={alertTooltip}>
                                        {BsMetaUtil.getName(metadata)}
                                        {alertTooltip && (
                                            <Icon
                                                sx={{ color: ({ palette: { error } }) => error.main }}
                                                type="warning"
                                            />
                                        )}
                                    </TooltipText>
                                    {pricesAvailable && !isWhirlpoolMetadata(metadata) && (
                                        <PriceComponent price={getPriceByMint(metadata.mint)} />
                                    )}
                                </Column>
                            </Row>
                            {(() => {
                                if (clmmPosition) {
                                    return (
                                        <Column alignItems="flex-end">
                                            <ClmmPositionInfo position={clmmPosition} />
                                            <PriceComponent price={clmmPosition.totalPrice} />
                                        </Column>
                                    );
                                }
                                if (BsMetaUtil.isFungible(metadata)) {
                                    return (
                                        <Column alignItems="flex-end">
                                            <Text>
                                                {BsMetaUtil.formatAmount(metadata, amount, {
                                                    hideSymbol: false
                                                })}
                                            </Text>
                                            {pricesAvailable && (
                                                <PriceComponent
                                                    price={bsMath.mul(getPriceByMint(metadata.mint), amount)}
                                                />
                                            )}
                                        </Column>
                                    );
                                }

                                return null;
                            })()}
                        </Row>
                    );
                })}
        </Column>
    );
}

export function TokenAmountWithUsd({
    amount,
    amountUsd,
    token,
    fontWeight,
    tokenSize = "sm",
    loading: propsLoading = false,
    display = "logo",
    variant,
    usdVariant,
    color,
    condensedOnMobile,
    shortSymbol = false,
    dot = false
}: {
    amount: number | undefined;
    amountUsd: number | undefined;
    token: TokenListMetadata | undefined;
    tokenSize?: TokenSize;
    loading?: boolean;
    display?: "logo" | "symbol" | "both";
    condensedOnMobile?: boolean;
    usdVariant?: TextVariant;
    shortSymbol?: boolean;
    color?: TextColor;
    dot?: boolean;
} & Pick<TextProps, "variant" | "fontWeight">) {
    const loading = propsLoading || amount === undefined || amountUsd === undefined;

    return (
        <Row spacing={dot ? 1 : 0.5}>
            {display !== "symbol" && <TokenImage size={tokenSize} metadata={token} />}
            <Text
                loadingWidth="60px"
                loading={loading}
                fontWeight={fontWeight}
                variant={variant}
                translate="no"
                color={color}
            >
                {BsMetaUtil.formatAmount(token, amount, {
                    hideSymbol: display === "logo",
                    decimals: condensedOnMobile ? 2 : undefined,
                    shortSymbol: shortSymbol
                })}
            </Text>
            {!loading && dot && <Dot color={BRAND_COLORS.GRAY} size={4} />}
            {!loading && (
                <Text
                    sx={condensedOnMobile ? { [MEDIA.SM.below]: { display: "none" } } : undefined}
                    fontWeight="normal"
                    color="caption"
                    variant={usdVariant || variant}
                    translate="no"
                >
                    {formatUsd(amountUsd)}
                </Text>
            )}
        </Row>
    );
}

export function PriceComponent({ price }: { price: number | undefined }) {
    return (
        <Text color="caption" variant="body2">
            {price ? formatUsd(price) : `No price found`}
        </Text>
    );
}

export function TokensTooltipDisplay({
    tokens: rawMetadata,
    sort = true,
    label,
    skeletonsCount
}: {
    tokens: { metadata: TokenListMetadata; rowEnd?: ReactNode }[] | undefined;
    label?: ReactNode;
    sort?: boolean;
    skeletonsCount?: number;
}) {
    const [search, setSearch] = useState<string>();
    const isMobile = useMediaQuery(MEDIA.SM.below);

    const metadataSorted = useMemo(
        () =>
            sort
                ? rawMetadata?.sort((a, b) =>
                      BsMetaUtil.getSymbolUnique(a.metadata).localeCompare(BsMetaUtil.getSymbolUnique(b.metadata))
                  )
                : rawMetadata,
        [rawMetadata, sort]
    );

    const metadata = useMemo(
        () =>
            metadataSorted?.filter(({ metadata }) => {
                if (!search) return true;
                return textContains(getSearchFromMetadata(metadata), search);
            }),
        [metadataSorted, search]
    );

    return (
        <Column spacing={1}>
            {!isMobile && greaterThan(rawMetadata?.length, 2) ? (
                <SearchInput
                    sx={{ ".MuiInputBase-root": { px: 0 }, svg: { fontSize: "inherit" } }}
                    hideBorder
                    forceFocus
                    placeholder="Search"
                    onChange={(e) => setSearch(e.target.value)}
                    value={search}
                />
            ) : (
                <Column />
            )}
            {label}

            <VerticalScroll maxHeight={300}>
                {metadata?.map(({ metadata, rowEnd }) => (
                    <Row spaceBetween key={metadata.mint} spacing={3}>
                        <Row sx={{ minWidth: "max-content" }} spacing={1}>
                            <TokenImage size="sm" metadata={metadata} />
                            <Text variant="body2">{BsMetaUtil.getSymbol(metadata)} </Text>
                        </Row>
                        {rowEnd}
                    </Row>
                ))}
                {!metadata && repeatElement(<SkeletonRounded width="100%" height={20} />, skeletonsCount)}
            </VerticalScroll>
        </Column>
    );
}

export function OverlappingMetadataImages({
    metadata,
    disableTooltip,
    sort = true,
    ...props
}: { metadata: TokenListMetadata[] | undefined; sort?: boolean; disableTooltip?: boolean } & Omit<
    OverlappingImagesProps,
    "images"
>) {
    const images = useMemo(
        () => metadata?.map((m) => ({ src: BsMetaUtil.getImage(m), variant: getImageStyle(m) })),
        [metadata]
    );

    const tooltipTokens = useMemo(() => metadata?.map((m) => ({ metadata: m })), [metadata]);

    return (
        <Tooltip
            reverseColors
            title={disableTooltip ? "" : <TokensTooltipDisplay sort={sort} tokens={tooltipTokens} />}
        >
            <OverlappingImages images={images} {...props} />
        </Tooltip>
    );
}

export type TokenSize = "xs" | "sm" | "md" | "lg" | "xl";

type TokenImageProps = {
    metadata: TokenListMetadata | undefined;
    size?: TokenSize | number;
    sx?: SxType;
};

export function getTokenImageSize(size: TokenSize) {
    switch (size) {
        case "xl":
            return 40;
        case "lg":
            return 30;
        case "md":
            return 20;
        case "sm":
            return FONT_SIZES.body1;
        case "xs":
            return 10;
        default:
            return 0;
    }
}

export function TokenImage({ metadata, size: propsSize = "md", sx }: TokenImageProps) {
    const size = typeof propsSize == "number" ? propsSize : getTokenImageSize(propsSize);

    return (
        <Image
            skeletonVariant="circular"
            variant={getImageStyle(metadata)}
            size={size + "px"}
            src={BsMetaUtil.getImage(metadata)}
            sx={sx}
        />
    );
}

const ROUNDED_TOKEN_CUSTODIANS = [
    "meTjtQikYwez7tL3LEi4zPPh4u5awMfXbprLZ18S1QS",
    "whUB5g126xgBTHYCr7EAACC7jT8snB7mexTKEWHSWGo"
];

function getImageStyle(metadata: TokenListMetadata | undefined): "rounded" | "circle" {
    if (metadata && ROUNDED_TOKEN_CUSTODIANS.includes(metadata?.custodian)) return "rounded";

    return "circle";
}

export function TokenSelect({
    tokens,
    value,
    setValue,
    getLabel
}: {
    tokens: TokenListMetadata[] | undefined;
    value: string | undefined;
    setValue: (value: string | undefined) => void;
    getLabel?: (metadata: TokenListMetadata) => string;
}) {
    return (
        <Select
            value={value}
            setValue={setValue}
            options={getFilterOptions(tokens ?? [], getLabel)}
            loading={!tokens}
        />
    );
}
const getFilterOptions = (
    tokens: TokenListMetadata[],
    getLabel: (metadata: TokenListMetadata) => string = BsMetaUtil.getSymbol
): SelectOption<string>[] => {
    const tokenOptions = removeDuplicatesByProperty(tokens, "mint").map((metadata) => ({
        value: metadata.mint,
        label: (
            <Row spacing={1}>
                <TokenImage metadata={metadata} /> <Text> {getLabel(metadata)} </Text>
            </Row>
        )
    }));
    return [{ value: ALL_VALUE, label: "All" }, ...tokenOptions];
};

export function TokenSolscanLink({ token }: { token: TokenListMetadata | undefined }): ReactElement {
    if (!token?.mint) {
        return <>{formatTokenName(token)}</>;
    }
    return (
        <TextExternalLink
            href={explorerTokenUrl(token.mint)}
            sx={{ color: "secondary.main", display: "inline" }}
            isPrimary
            underlineLink
        >
            {formatTokenName(token)}
        </TextExternalLink>
    );
}

interface GetAssetButtonProps {
    routeType?: LoopRouteType | null;
    buyToken: TokenListMetadata | undefined;
    sellToken: TokenListMetadata | undefined;
    isWhirlpoolPoolSelectedInput?: boolean;
    supportingToken?: TokenListMetadata | undefined;
    stabblePoolLink?: string;
    variant?: TextVariant;
    color?: TextColor;
    swapOnClick?: () => void;
}

export function GetAssetButton({
    routeType,
    buyToken,
    sellToken,
    supportingToken,
    stabblePoolLink,
    isWhirlpoolPoolSelectedInput = false,
    variant = "body2",
    color = "primary",
    swapOnClick
}: GetAssetButtonProps) {
    const { open } = useAppDialog();

    if (
        !buyToken ||
        !sellToken ||
        (isWhirlpoolPoolSelectedInput && !supportingToken) ||
        (routeType === LoopRouteType.Stabble && !stabblePoolLink)
    )
        return null;

    const orcaLink = isWhirlpoolPoolSelectedInput
        ? `https://orca.so/pools/${buyToken?.mint}`
        : `https://jup.ag/swap/SOL-${BsMetaUtil.getSymbol(supportingToken)}`;

    const customSwapUrl = sellToken?.assetSwapUrl;

    if (customSwapUrl) {
        return (
            <ExternalLink target="_blank" rel="noopener" href={customSwapUrl}>
                <TextButton color={color} variant={variant}>
                    Get {BsMetaUtil.getSymbol(buyToken)}
                </TextButton>
            </ExternalLink>
        );
    }

    switch (routeType) {
        case LoopRouteType.Stabble:
            return (
                <ExternalLink target="_blank" rel="noopener" href={stabblePoolLink}>
                    <TextButton color={color} variant={variant}>
                        Get {BsMetaUtil.getSymbol(buyToken)}
                        <ArrowOutwardOutlined />
                    </TextButton>
                </ExternalLink>
            );
        case LoopRouteType.Exponent:
            return (
                <ExternalLink target="_blank" rel="noopener" href={getExponentFinanceIncomeUrl(buyToken)}>
                    <TextButton color="primary" variant="body2">
                        Get {BsMetaUtil.getSymbol(buyToken)}
                        <ArrowOutwardOutlined />
                    </TextButton>
                </ExternalLink>
            );
        case LoopRouteType.Ratex:
            return (
                <ExternalLink target="_blank" rel="noopener" href={`https://app.rate-x.io/earn/fixed-yield`}>
                    <TextButton color={color} variant={variant}>
                        Get PT-{BsMetaUtil.getSymbol(buyToken)}
                        <ArrowOutwardOutlined />
                    </TextButton>
                </ExternalLink>
            );
        case LoopRouteType.Flash:
            return (
                <ExternalLink target="_blank" rel="noopener" href={`https://beast.flash.trade/earn`}>
                    <TextButton color={color} variant={variant}>
                        Get {BsMetaUtil.getSymbol(buyToken)}
                        <ArrowOutwardOutlined />
                    </TextButton>
                </ExternalLink>
            );
        case LoopRouteType.Adrena:
            return (
                <ExternalLink target="_blank" rel="noopener" href={`https://app.adrena.xyz/buy_alp`}>
                    <TextButton color={color} variant={variant}>
                        Get {BsMetaUtil.getSymbol(buyToken)}
                        <ArrowOutwardOutlined />
                    </TextButton>
                </ExternalLink>
            );
        case LoopRouteType.Orca:
            return (
                <ExternalLink target="_blank" rel="noopener" href={orcaLink}>
                    <TextButton color={color} variant={variant}>
                        Get {BsMetaUtil.getSymbol(buyToken)}
                        <ArrowOutwardOutlined />
                    </TextButton>
                </ExternalLink>
            );
        default:
            return (
                <TextButton
                    color={"secondary"}
                    variant={"body2"}
                    onClick={() =>
                        swapOnClick ? swapOnClick() : open(AppDialog.Swap, { buyToken, sellToken, routeType })
                    }
                >
                    Buy {BsMetaUtil.getSymbol(buyToken)}
                    <ArrowOutwardOutlined />
                </TextButton>
            );
    }
}

function getExponentFinanceIncomeUrl(buyToken?: TokenListMetadata) {
    if (!buyToken?.name) return undefined;
    const base = buyToken.name.replace("Exponent ", "");
    // Look for the pattern with month as first uppercase, rest lowercase
    const urlPart = base
        .replace(/(\d{2})([A-Za-z]{3})(\d{2})/, (match, day, month, year) => {
            return `${day}${month.charAt(0).toUpperCase()}${month.slice(1).toLowerCase()}${year}`;
        })
        .toLowerCase();
    // Restore case only for the month substring
    const match = base.match(/(\d{2})([A-Za-z]{3})(\d{2})/);
    let finalUrlPart = urlPart;
    if (match) {
        // Replace -xxmayyy with -xxMayyy, while keeping rest lowercase
        const patternLower = `${match[1]}${match[2].toLowerCase()}${match[3]}`;
        const patternCorrect = `${match[1]}${match[2].charAt(0).toUpperCase()}${match[2].slice(1).toLowerCase()}${
            match[3]
        }`;
        finalUrlPart = urlPart.replace(patternLower, patternCorrect);
    }
    return `https://app.exponent.finance/income/${finalUrlPart}?preserveScroll=true`;
}
